import { FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { solicitarRecuperacion } from "@/lib/api";
import { extractApiError } from "@/lib/utils";

export function LoginPage() {
  const { sesion, iniciar, cargando } = useAuth();
  const location = useLocation();

  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [verPassword, setVerPassword] = useState(false);
  const [ayudaRecuperar, setAyudaRecuperar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (sesion) {
    const desde = (location.state as { from?: string } | null)?.from || "/";
    return <Navigate to={desde} replace />;
  }

  async function manejarEnvio(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      // El rol ya no se envía: lo determina el servidor a partir de la
      // cuenta. Antes era un desplegable y cualquiera podía elegir
      // "administrador".
      await iniciar(usuario.trim(), password);
    } catch (err) {
      setError(extractApiError(err, "No se pudo iniciar sesión."));
    }
  }

  return (
    <div
      className="min-h-screen grid place-items-center"
      style={{ background: "var(--bg-2)" }}
    >
      <div className="w-full max-w-md p-10 stack stack-5">
        <div className="text-center stack stack-3">
          <div className="brand-mark mx-auto" style={{ width: 44, height: 44, fontSize: 16 }}>G</div>
          <div>
            <h1
              style={{
                fontFamily: "var(--font-serif)",
                fontSize: 34,
                fontWeight: 500,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              GEMA
            </h1>
            <p style={{ color: "var(--ink-3)", fontSize: 14, margin: 0 }}>
              Backoffice del motor PQRS
            </p>
          </div>
        </div>

        <form onSubmit={manejarEnvio} className="stack stack-4">
          <div className="field">
            <label htmlFor="usuario">Usuario</label>
            <input
              id="usuario"
              type="text"
              required
              autoFocus
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              className="input"
              placeholder="ana.gomez"
              autoComplete="username"
            />
          </div>

          <div className="field">
            <label htmlFor="password">Contraseña</label>
            <div style={{ position: "relative" }}>
              <input
                id="password"
                type={verPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                autoComplete="current-password"
                style={{ paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setVerPassword((v) => !v)}
                aria-label={verPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                title={verPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                style={{
                  position: "absolute",
                  right: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--ink-3)",
                  padding: 4,
                  lineHeight: 0,
                }}
              >
                {verPassword ? <IconoOjoTachado /> : <IconoOjo />}
              </button>
            </div>
            <span className="hint">
              Tras cinco intentos fallidos la cuenta se bloquea 15 minutos.
            </span>
          </div>

          <button
            type="button"
            className="link-plano"
            onClick={() => setAyudaRecuperar((v) => !v)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "var(--emerald-deep)",
              fontSize: 13,
              textAlign: "left",
            }}
          >
            ¿Olvidaste tu contraseña?
          </button>

          {ayudaRecuperar && <RecuperacionInline />}

          {error && (
            <div className="banner banner--red">
              <span className="banner__icon">⚠</span>
              <div>{error}</div>
            </div>
          )}

          <button
            type="submit"
            className="btn btn--primary btn--block btn--lg"
            disabled={cargando || !usuario || !password}
          >
            {cargando ? "Ingresando…" : "Ingresar"}
          </button>
        </form>

        <p
          className="muted text-center"
          style={{ fontSize: 12, marginTop: 0 }}
        >
          ¿Sin cuenta? Solicítala a un administrador.
        </p>
      </div>
    </div>
  );
}

/** Formulario inline de "olvidé mi contraseña": pide el enlace por correo. */
function RecuperacionInline() {
  const [ident, setIdent] = useState("");
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  async function enviar() {
    if (!ident.trim() || enviando) return;
    setEnviando(true);
    try {
      await solicitarRecuperacion(ident.trim());
      setEnviado(true);
    } catch {
      // El backend siempre responde 200; un error aquí es de red.
      setEnviado(true);
    } finally {
      setEnviando(false);
    }
  }

  if (enviado) {
    return (
      <div className="banner banner--green">
        <span className="banner__icon">✓</span>
        <div style={{ fontSize: 13 }}>
          Si la cuenta existe y tiene un correo registrado, te enviamos un
          enlace para restablecer la contraseña. Revisa tu bandeja (y el
          spam). El enlace vence en una hora.
        </div>
      </div>
    );
  }

  return (
    <div className="banner banner--blue">
      <span className="banner__icon">🔑</span>
      <div style={{ fontSize: 13, width: "100%" }}>
        <p style={{ margin: "0 0 8px" }}>
          Escribe tu usuario o correo y te enviaremos un enlace para
          restablecerla.
        </p>
        <div className="row" style={{ gap: 8 }}>
          <input
            className="input"
            value={ident}
            onChange={(e) => setIdent(e.target.value)}
            placeholder="usuario o correo"
            autoComplete="username"
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), enviar())}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={enviar}
            disabled={!ident.trim() || enviando}
          >
            {enviando ? "Enviando…" : "Enviar"}
          </button>
        </div>
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--ink-3)" }}>
          ¿Sin correo registrado? Pídele a un administrador que la resetee.
        </p>
      </div>
    </div>
  );
}

function IconoOjo() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconoOjoTachado() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
